'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import {
  ParagraphCollectionFragment,
  RichTextFragment,
  SingleLineFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { TrackingConversionType } from '@/utils/tracking-conversion-types';
import { buildTrackingId, trackConversion } from '@/utils/tracking-helper';
import EmailHelper from '@/utils/email-helper';
import Container from '@/ui/components/shared/Container';
import { ParagraphCollectionRenderer } from '@/ui/components/tileRenderers/ParagraphCollectionRenderer/ParagraphCollectionRenderer';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import TextInput from '@/ui/components/shared/inputs/TextInput';
import Button from '@/ui/components/shared/Button';
import { submitRmaFreeTrialForm } from './rma-free-trial-form-actions';
import { RMA_REGISTRATION_ERROR } from './rma-free-trial-form-types';

export type RmaFreeTrialFormTile = {
  'description-text': ParagraphCollectionFragment;
  'success-text': ParagraphCollectionFragment;
  'nuki-id': SingleLineFragment;
  'email-address': SingleLineFragment;
  'first-name': SingleLineFragment;
  'last-name': SingleLineFragment;
  'legal-text': RichTextFragment;
  'submit-button-text'?: SingleLineFragment;
};

interface Props {
  tile: RmaFreeTrialFormTile;
  trackingPrefix?: string;
}

interface FormData {
  email: string;
  firstName?: string;
  lastName?: string;
  nukiId: string;
  deviceUuid: string;
  privacyPolicy: boolean;
}

export function RmaFreeTrialForm({ tile, trackingPrefix }: Readonly<Props>) {
  const t = useTranslations('RmaFreeTrialForm');

  const searchParams = useSearchParams();
  const nukiIdFromUrl = searchParams.get('nukiId') ?? '';
  const deviceUuidFromUrl = searchParams.get('ndt') ?? '';

  const [isSubmissionSuccess, setIsSubmissionSuccess] = useState(false);
  const [submissionError, setSubmissionError] = useState('');

  const validationSchema = yup.object({
    email: yup
      .string()
      .email(t('formInvalidEmail'))
      .test('has-tld', t('formInvalidEmail'), (value) => {
        if (!value) {
          return true;
        }

        return EmailHelper.isValid(value);
      })
      .required(t('formRequiredEmail')),
    firstName: yup.string().max(50, t('formMaxLengthExceeded')),
    lastName: yup.string().max(50, t('formMaxLengthExceeded')),
    nukiId: yup.string().required(t('formInvalidNukiId')),
    deviceUuid: yup.string().required(t('formInvalidDeviceUuid')),
    privacyPolicy: yup
      .boolean()
      .oneOf([true], t('formRequiredPrivacyPolicy'))
      .required(t('formRequiredPrivacyPolicy')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      nukiId: nukiIdFromUrl,
      deviceUuid: deviceUuidFromUrl,
      privacyPolicy: false,
    },
  });

  const onSubmit = async (data: FormData) => {
    setSubmissionError('');

    const result = await submitRmaFreeTrialForm({
      nukiId: data.nukiId,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      deviceUuid: data.deviceUuid,
    });

    if (result.success) {
      trackConversion(TrackingConversionType.ACTIVATION_CODE_REQUESTED, {
        ndt: data.deviceUuid,
        nukiId: Number(data.nukiId).toString(16).toUpperCase(),
      });

      globalThis.location.hash = 'close'; // needed for iOS
      const { pathname, search } = globalThis.location;
      globalThis.history.replaceState(null, '', `${pathname}${search}#close`); // keeps #close in URL

      setIsSubmissionSuccess(true);
      return;
    }

    if (result.error === RMA_REGISTRATION_ERROR.ALREADY_ACTIVATED) {
      setSubmissionError(t('formErrorAlreadyActivated'));
      return;
    }

    setSubmissionError(t('formErrorSubmissionFailed'));
  };

  if (isSubmissionSuccess) {
    return (
      <Container className="overflow-hidden">
        <div className="mx-auto max-w-3xl text-center">
          <ParagraphCollectionRenderer
            content={tile['success-text']}
            titleClassName="text-3xl leading-tight mb-5 max-w-xl mx-auto"
            bodyClassName="text-2xl max-w-3xl mx-auto"
            highlightTitlePunctuation
            removeParagraphMargins
          />
        </div>
      </Container>
    );
  }

  return (
    <Container className="overflow-hidden">
      <div className="mx-auto max-w-3xl rounded-lg p-6 sm:p-10">
        <div className="mx-auto mb-8 max-w-3xl text-center lg:mb-10">
          <ParagraphCollectionRenderer
            content={tile['description-text']}
            titleClassName="text-3xl leading-tight mb-4  max-w-xl mx-auto"
            bodyClassName="text-2xl text-2xl max-w-3xl mx-auto"
            highlightTitlePunctuation
            removeParagraphMargins
          />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mx-auto max-w-lg space-y-8" noValidate>
          <TextInput
            id="email"
            register={register}
            errors={errors.email}
            label={tile['email-address']?.text ?? t('formLabelEmail')}
            placeholder={t('formPlaceholderEmail')}
            required
            type="email"
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextInput
              id="firstName"
              register={register}
              errors={errors.firstName}
              label={tile['first-name']?.text ?? t('formLabelFirstName')}
              placeholder={t('formPlaceholderFirstName')}
            />
            <TextInput
              id="lastName"
              register={register}
              errors={errors.lastName}
              label={tile['last-name']?.text ?? t('formLabelLastName')}
              placeholder={t('formPlaceholderLastName')}
            />
          </div>

          <input type="hidden" {...register('nukiId')} />
          <input type="hidden" {...register('deviceUuid')} />

          <div className="mb-16">
            <label htmlFor="privacyPolicy" className="flex cursor-pointer items-center gap-2">
              <input
                id="privacyPolicy"
                type="checkbox"
                {...register('privacyPolicy')}
                className="h-4 w-4 cursor-pointer"
                aria-describedby="privacyPolicy-error"
              />
              <span className="text-neutral-55">
                <RichTextRenderer blocks={tile['legal-text']} />
              </span>
            </label>
            {errors.privacyPolicy && (
              <p id="privacyPolicy-error" className="text-sm text-red">
                {errors.privacyPolicy.message}
              </p>
            )}
          </div>

          {errors.nukiId && <p className="text-sm text-red">{errors.nukiId.message}</p>}
          {errors.deviceUuid && <p className="text-sm text-red">{errors.deviceUuid.message}</p>}

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting}
            loading={isSubmitting}
            data-tile-element-id={buildTrackingId(trackingPrefix, 'submit')}
          >
            {tile['submit-button-text']?.text ?? t('formSubmitBtn')}
          </Button>

          {submissionError && <p className="text-center text-sm text-red">{submissionError}</p>}
        </form>
      </div>
    </Container>
  );
}
